import { FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import { getDftTotalReturnsData } from "@app/components/ChartDftTotalReturns";
import { typographyOptions } from "@app/theme/typography";
import { formatPercent } from "@app/utils";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { addYears, getYear } from "date-fns";
import { CarAssetClassDftItem } from "../useAssetClassSummaryStat";

interface DftTotalReturnsPdfChartProps
  extends Omit<CarPdfChartProps, "options" | "data" | "plugins" | "type"> {
  dftItem: CarAssetClassDftItem;
  isWhiteContext?: boolean;
}

// extending grid by 20 pixels an moving it into right page padding,
// in order to align roundedBackground with other elements on the page
const RIGHT_OFFSET = 20;

export const DftTotalReturnsPdfChart = ({
  dftItem,
  isWhiteContext,
  ...props
}: DftTotalReturnsPdfChartProps) => {
  const theme = useTheme();

  const calcData = getDftTotalReturnsData({
    theme,
    dftItem,
  });

  if (!calcData) {
    return <></>;
  }

  const { datasets, yLabel } = calcData;

  const font: Partial<FontSpec> = {
    size: 9,
    weight: 400,
    lineHeight: 1,
    family: typographyOptions.fontFamily,
  };

  return (
    <CarPdfChart
      type="line"
      data={{
        datasets,
      }}
      options={{
        layout: {
          padding: {
            right: RIGHT_OFFSET + 2,
          },
        },
        scales: {
          x: {
            title: {
              display: false,
            },
            type: "time",
            ticks: {
              display: true,
              padding: 0,
              font,
              callback: (value) => {
                const year = getYear(new Date(value));
                return year % 5 === 0 ? year : undefined;
              },
            },
            grid: {
              display: false,
            },
            time: {
              unit: "year",
            },
          },
          y: {
            type: "logarithmic",
            ticks: {
              display: true,
              font,
              padding: 0,
              color: theme.palette.softBlack,
              callback: (value, index, values) => {
                if (index === 0 || index === values.length - 1) {
                  return undefined;
                }
                return typeof value === "number"
                  ? formatPercent(value, 1)
                  : value;
              },
            },
            grid: ChartUtils.getGridOptions({ theme }),
            title: {
              display: true,
              text: yLabel,
              color: theme.palette.softBlack,
              font: {
                ...font,
                size: 11,
                weight: 600,
              },
            },
          },
        },
        plugins: {
          periods: {
            backgroundColor: "rgba(236, 238, 240, 0.5)",
            borderColor: "rgba(178, 179, 181, 0.5)",
            font: {
              ...font,
              style: "italic",
              size: 8,
            },
            textColor: theme.palette.black,
            labelPosition: "horizontal",
            items: [
              {
                start: addYears(new Date(), -10).getTime(),
                end: new Date().getTime(),
                label: "Timeframe too short for 10 year analysis",
              },
            ],
          },
          roundedBackground: ChartUtils.getRoundedBackgroundOptions({
            theme,
            isWhiteContext,
          }),
        },
      }}
      plugins={[ChartPlugins.roundedBackground, ChartPlugins.periods]}
      {...props}
      width={props.width + RIGHT_OFFSET}
      style={{
        ...props.style,
        marginRight: -RIGHT_OFFSET,
      }}
    />
  );
};
